<template>
  <div class="flex">
    <div class="flex gap-64">
      <div class="flex gap-8 items-center justify-center">
        <SparkSelect
          ref="classSelect"
          v-model="filters.mat_lvl_4"
          custom="!w-[200px]"
          class="!mb-0"
          :options="loadingClass ? [{ label: 'Loading...', value: null, disabled: true }] : classOptions"
          placeholder="Select a Class"
          name="selection"
        />
        <SparkSelect
          ref="materialSelect"
          v-model="filters.mat_id"
          custom="!w-[200px]"
          class="!mb-0"
          :options="loadingMaterial ? [{ label: 'Loading...', value: null, disabled: true }] : materialOptions"
          placeholder="Select a Material"
          name="selection"
        />
        <SparkSelect
          ref="technologySelect"
          v-model="filters.prc_acronym"
          custom="!w-[200px]"
          class="!mb-0"
          :options="loadingTechnology ? [{ label: 'Loading...', value: null, disabled: true }] : prcAcronymOptions"
          placeholder="Select a Technology"
          name="selection"
        />

        <SparkButton class="!w-25 items-center" variant="outlined" @click="clearFilters">
          <span>Clear</span>
        </SparkButton>

        <SparkButton
          class="whitespace-nowrap min-w-fit w-150"
          variant="secondary"
          :disabled="requestRunning"
          @click="sendData"
        >
          <div>
            <img v-if="requestRunning" src="@/assets/img/loading.gif" class="w-20 h-20 ml-auto mr-auto" />
            <p v-else>Update</p>
          </div>
        </SparkButton>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import SparkButton from '../../SparkComponents/SparkButton.vue';

import SparkSelect from '@/components/SparkComponents/SparkSelect.vue';

export default {
  name: 'PartActionButtons',

  components: { SparkButton, SparkSelect },

  data() {
    return {
      prcAcronymOptions: [],
      materialOptions: [],
      classOptions: [],
      loadingClass: false,
      loadingMaterial: false,
      loadingTechnology: false,
      materialRequestId: 0,    // To track the latest request
      classRequestId: 0,       // To track the latest request
      technologyRequestId: 0,  // To track the latest request
    };
  },

  computed: {
    ...mapState(['part']),
    ...mapState('application', ['axiosInstance']),
    ...mapState('supplier', ['selectedFilters', 'requestRunning']),

    filters: {
      get() {
        return this.selectedFilters;
      },

      set(newVal) {
        this.setSelectedFilters(newVal);
      },
    },
  },

  watch: {
    'filters.mat_lvl_4': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$nextTick(() => {
            this.fetchMaterials();
            this.fetchPrcAcronyms();
          });
        }
      },
    },
    'filters.mat_id': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$nextTick(() => {
            this.fetchClasses();
            this.fetchPrcAcronyms();
          });
        }
      },
    },
    'filters.prc_acronym': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$nextTick(() => {
            this.fetchClasses();
            this.fetchMaterials();
          });
        }
      },
    },
    prcAcronymOptions: {
      handler(newOptions) {
        if (newOptions.length > 0 && this.filters.prc_acronym) {
          const matchingOption = newOptions.find(option => option.value === this.filters.prc_acronym);
          if (!matchingOption) {
            this.setSelectedFilters({
              ...this.filters,
              prc_acronym: null,
            });
          }
        }
      },
    },
  },

  mounted() {
    this.fetchClasses().then(() => {
      this.fetchMaterials().then(() => {
        this.fetchPrcAcronyms().then(() => {
          this.requestPriceData({
            partId: this.part.part_id,
            data: {
              mat_lvl_4: this.filters.mat_lvl_4,
              mat_id: this.filters.mat_id,
              prc_acronym: this.filters.prc_acronym,
            },
          });
        });
      });
    });
  },

  beforeUnmount() {
    this.clearFilters();
  },

  methods: {
    ...mapMutations('supplier', ['setSelectedFilters']),
    ...mapActions('supplier', ['requestPriceData']),

    async fetchClasses() {
      this.loadingClass = true;
      const requestId = ++this.classRequestId;
      const prefiltered = true;
      const params = {
        prefiltered,
        mat_id: this.filters.mat_id,
        prc_acronym: this.filters.prc_acronym,
      };

      try {
        const response = await this.axiosInstance.get('api/v1/material-classes-dropdown/', { params });
        if (requestId === this.classRequestId) {
          this.classOptions = response.data.map(entry => ({
            value: entry.id,
            label: entry.name,
          }));
          this.classOptions.unshift({ label: '', value: null });
        }
        return response;
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        if (requestId === this.classRequestId) {
          this.loadingClass = false;
        }
      }
    },

    async fetchMaterials() {
      this.loadingMaterial = true;
      const requestId = ++this.materialRequestId;
      const prefiltered = true;
      const distinct = true;
      const params = {
        prefiltered,
        distinct,
        mat_lvl_4: this.filters.mat_lvl_4,
        prc_acronym: this.filters.prc_acronym,
      };

      try {
        const response = await this.axiosInstance.get('api/v1/default-materials/', { params });
        if (requestId === this.materialRequestId) {
          this.materialOptions = response.data.map(entry => ({
            value: entry.mat_id,
            label: entry.mat_name,
          }));
          this.materialOptions.unshift({ label: '', value: null });
        }
        return response;
      } catch (error) {
        console.error('Error fetching materials:', error);
      } finally {
        if (requestId === this.materialRequestId) {
          this.loadingMaterial = false;
        }
      }
    },

    async fetchPrcAcronyms() {
      this.loadingTechnology = true;
      const requestId = ++this.technologyRequestId;
      const prefiltered = true;
      const params = {
        prefiltered,
        mat_lvl_4: this.filters.mat_lvl_4,
        mat_id: this.filters.mat_id || undefined,
      };

      try {
        const response = await this.axiosInstance.get('api/v1/supplier-prc-acronyms/', { params });
        if (requestId === this.technologyRequestId) {
          // Only use the main process acronym and name, ignore synonyms
          const seen = new Set();
          const options = response.data.map(entry => {
            return {
              value: entry.prc_acronym,
              label: entry.prc_name,
            };
          }).filter(option => {
            if (!option.value || seen.has(option.value)) return false;
            seen.add(option.value);
            return true;
          });
          options.unshift({ label: '', value: null });
          this.prcAcronymOptions = options;
        }
        return response;
      } catch (error) {
        console.error('Error fetching technologies:', error);
      } finally {
        if (requestId === this.technologyRequestId) {
          this.loadingTechnology = false;
        }
      }
    },

    async sendData() {
      if (!this.requestRunning) {
        const data = {
          mat_lvl_4: this.filters.mat_lvl_4,
          mat_id: this.filters.mat_id,
          prc_acronym: this.filters.prc_acronym,
        };

        this.requestPriceData({ partId: this.part.part_id, data });
      }
    },

    clearFilters() {
      if (this.$refs.classSelect) this.$refs.classSelect.searchTerm = '';
      if (this.$refs.materialSelect) this.$refs.materialSelect.searchTerm = '';
      if (this.$refs.technologySelect) this.$refs.technologySelect.searchTerm = '';

      this.setSelectedFilters({
        ...this.filters,
        mat_lvl_4: null,
        mat_id: null,
        prc_acronym: null,
      });

      this.requestPriceData({
        partId: this.part.part_id,
        data: {
          mat_lvl_4: null,
          mat_id: null,
          prc_acronym: null,
        },
      });

      this.$nextTick(() => {
        this.fetchClasses();
        this.fetchMaterials();
        this.fetchPrcAcronyms();
      });
    },
  },
};
</script>
